import React from 'react';
import { formatMoney } from '../../../../utils/numberFormatting';

const StatsItem = ({ label, value, isLoading = false }) => {
    if (isLoading) {
        return (
            <div className="animate-pulse">
                <div className="h-4 bg-slate-800 rounded w-24 mb-2" />
                <div className="h-8 bg-slate-800 rounded w-32" />
            </div>
        );
    }

    return (
        <div>
            <div className="text-slate-400 text-sm mb-1">{label}</div>
            <div className="text-2xl font-bold tabular-nums text-slate-300/90">{value}</div>
        </div>
    );
};

const StatsItemWithBadge = ({ label, value, rValue, isProfit, isLoss, isLoading = false }) => {
    if (isLoading) {
        return (
            <div className="animate-pulse">
                <div className="h-4 bg-slate-800 rounded w-24 mb-2" />
                <div className="h-8 bg-slate-800 rounded w-32" />
            </div>
        );
    }

    const badgeColor = isProfit
        ? 'bg-emerald-500/20 text-emerald-400'
        : isLoss
            ? 'bg-red-500/20 text-red-400'
            : 'bg-slate-500/20 text-slate-400';

    const valueColor = isProfit
        ? 'text-emerald-500'
        : isLoss
            ? 'text-red-500'
            : 'text-slate-300/90';

    const rPrefix = isProfit ? '+' : isLoss ? '' : '';

    return (
        <div>
            <div className="text-slate-400 text-sm mb-1">{label}</div>
            <div className="flex items-center gap-2">
                <span className={`text-2xl font-bold tabular-nums ${valueColor}`}>{value}</span>
                <span className={`${badgeColor} text-xs font-medium px-2 py-0.5 rounded`}>
                    {rPrefix}{rValue}R
                </span>
            </div>
        </div>
    );
};

const MatrixStats = ({ stats, isLoading = false }) => {
    if (isLoading) {
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
                {[...Array(8)].map((_, index) => (
                    <StatsItem key={index} isLoading={true} />
                ))}
            </div>
        );
    }

    const {
        totalMoney,
        totalR,
        wins,
        losses,
        profitFactor,
        maxWinStreak,
        maxLossStreak,
        maxLossDays,
        maxDrawdown
    } = stats;

    const isProfit = Number(totalMoney) > 0;
    const isLoss = Number(totalMoney) < 0;

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
            <StatsItemWithBadge
                label="Net Profit"
                value={formatMoney(totalMoney)}
                rValue={totalR}
                isProfit={isProfit}
                isLoss={isLoss}
            />
            <StatsItem
                label="Winning Trades"
                value={wins.toLocaleString()}
            />
            <StatsItem
                label="Losing Trades"
                value={losses.toLocaleString()}
            />
            <StatsItem
                label="Profit Factor"
                value={profitFactor}
            />
            <StatsItemWithBadge
                label="Maximum Drawdown"
                value={formatMoney(maxDrawdown)}
                rValue={maxLossStreak}
                isLoss={true}
            />
            <StatsItem
                label="Max Win Streak"
                value={maxWinStreak.toLocaleString()}
            />
            <StatsItem
                label="Max Loss Streak"
                value={maxLossStreak.toLocaleString()}
            />
            <StatsItem
                label="Max Loss Days"
                value={maxLossDays.toLocaleString()}
            />
        </div>
    );
};

export default MatrixStats;